import PropTypes from 'prop-types';
import { Markdown } from '@zedoc/react-markdown';
import React, { useState, useRef, useMemo } from 'react';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline';
import { Bold, Italic, Link as LinkIcon } from 'styled-icons/boxicons-regular';
import Tooltip from '../common/components/Tooltip';
import FormFieldMultiLanguage from './forms/FormFieldMultiLanguage';

const ToolbarButton = ({ children, title, onClick }) => (
  <Tooltip title={title}>
    <button
      type="button"
      onClick={onClick}
      className="w-8 h-8 border border-divider rounded-md text-xs font-medium hover:bg-gray-100"
    >
      {children}
    </button>
  </Tooltip>
);

ToolbarButton.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  onClick: PropTypes.func,
};

ToolbarButton.defaultProps = {
  title: null,
  onClick: () => {},
};

// TODO: I need to update value / onChange logic depending on "isMultiLanguage"
// for now I just hide the language dropdown

const MarkdownEditor = ({ value, onChange, limit = 300, isMultiLanguage }) => {
  const textareaRef = useRef();

  const [language, setLanguage] = useState('en');

  const localValue = useMemo(() => value?.[language], [value, language]);

  const handleChange = (val) => onChange({ ...value, [language]: val });

  const wrapSelection = (before, after) => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = localValue.slice(start, end);
    const newValue =
      localValue.slice(0, start) +
      before +
      selected +
      after +
      localValue.slice(end);
    handleChange(newValue);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start + before.length;
      textarea.selectionEnd = end + before.length;
    });
  };

  const handleInputChange = (e) => {
    const text = e.target.value.slice(0, limit);
    handleChange(text);
  };

  const [showPreview, setShowPreview] = useState(false);

  const handleLanguageChange = (lng) => {
    setLanguage(lng);

    if (!(lng in value)) {
      onChange({ ...value, [lng]: '' });
    }

    textareaRef.current.focus();
  };

  return (
    <div className="space-y-2">
      {/* Toolbar */}
      <div className="cluster-2 justify-between items-center">
        <div className="cluster-2">
          <ToolbarButton title="Bold" onClick={() => wrapSelection('**', '**')}>
            <Bold size="1rem" />
          </ToolbarButton>
          <ToolbarButton title="Italic" onClick={() => wrapSelection('_', '_')}>
            <Italic size="1rem" />
          </ToolbarButton>
          <ToolbarButton
            title="Link"
            onClick={() => wrapSelection('[', '](url)')}
          >
            <LinkIcon size="1rem" />
          </ToolbarButton>
          <ToolbarButton onClick={() => wrapSelection('# ', '\n')}>
            H1
          </ToolbarButton>
          <ToolbarButton onClick={() => wrapSelection('## ', '\n')}>
            H2
          </ToolbarButton>
          <ToolbarButton onClick={() => wrapSelection('### ', '\n')}>
            H3
          </ToolbarButton>
        </div>

        <Tooltip title={showPreview ? 'Hide Preview' : 'Show Preview'}>
          <button
            type="button"
            className="w-8 h-8 flex justify-center items-center border border-divider rounded-md hover:bg-gray-100"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? (
              <EyeOffIcon className="w-4 h-4" />
            ) : (
              <EyeIcon className="w-4 h-4" />
            )}
          </button>
        </Tooltip>
      </div>

      <div
        className={`grid gap-2 ${showPreview ? 'grid-cols-2' : 'grid-cols-1'}`}
      >
        <textarea
          ref={textareaRef}
          value={localValue}
          onChange={handleInputChange}
          rows={4}
          className="w-full border border-divider rounded-md p-2 focus:outline-none focus:ring"
        />
        {showPreview && (
          <div className="p-2 border border-divider rounded-md bg-gray-50 cursor-default">
            <Markdown source={localValue} />
          </div>
        )}
      </div>

      <div className="flex justify-between items-center">
        {isMultiLanguage ? (
          <FormFieldMultiLanguage
            languagesValues={value}
            language={language}
            onSelectLanguage={handleLanguageChange}
          />
        ) : (
          <div />
        )}
        <span className="text-sm text-secondary">
          {localValue ? localValue.length : '0'}/{limit} characters
        </span>
      </div>
    </div>
  );
};

MarkdownEditor.propTypes = {
  value: PropTypes.objectOf(PropTypes.string),
  onChange: PropTypes.func,
  limit: PropTypes.number,
  isMultiLanguage: PropTypes.bool,
};

MarkdownEditor.defaultProps = {
  value: null,
  onChange: () => {},
  limit: 300,
  isMultiLanguage: false,
};

export default MarkdownEditor;
